import type { ManualChapter, ManualSection } from '../types/help-center.types';

export const manualChapters: ManualChapter[] = [
  {
    id: 'cap-1',
    number: 1,
    title: '¿Qué es Prixia?',
    content: `Prixia es la plataforma de precios de tu empresa. Junta en un solo lugar tres módulos:

- Cotiza: cotizaciones con modelos de precio, márgenes y aprobaciones. Incluye la gestión comercial (clientes, oportunidades, cotizaciones y contratos) y la analítica.
- Compite: tus precios contra los de la competencia. Monitoreo de tiendas, análisis de mercado, reglas que ningún precio puede pasar y estrategias que proponen precios para que decidas.
- Comercia: el costo real de importar. Cotizador de flete con el tarifario vigente, embarques, contenedores y el asistente arancelario.

Y dos áreas de soporte:
- Datos maestros: divisas, tipos de cambio, líneas de negocio, países, materiales y flete.
- Mi empresa: usuarios, roles, equipos, aprobaciones, documentos y configuración.

[img:platform-modules]

Cada empresa tiene sus datos completamente aislados: nadie de otra empresa ve tus precios, tus clientes ni tus cotizaciones.

[nota]Lo que ves en el menú depende de los permisos de tu rol: si un módulo no aparece, pedile acceso al administrador de tu empresa.[/nota]`,
  },
  {
    id: 'cap-2',
    number: 2,
    title: 'Acceso al sistema',
    content: `Para entrar a Prixia:

[img:login-flow]

Ingreso:
Así entrás cada día a trabajar.

1. Abrí la página de inicio de sesión.
2. Escribí tu correo y tu contraseña.
3. Entrás al Inicio de tu empresa. Si trabajás en más de una, primero elegís en cuál.

Registro de una empresa nueva:
Se hace una sola vez, y quien registra la empresa queda como su administrador.

1. Abrí la página de registro.
2. Elegí el tipo de negocio: Prixia se adapta a tu rubro.
3. Elegí el país donde operás, para las monedas y los impuestos.
4. Si querés, indicá cuántas personas trabajan en la empresa (es opcional).
5. Completá los datos de la empresa: el nombre y, si querés, el NIT y el teléfono.
6. Creá tu cuenta de acceso: nombre, correo y contraseña. Se crea la empresa y quedás como su administrador.

[tip]Después podés invitar a tu equipo desde Mi empresa › Usuarios, o crear un link de invitación para que se registren solos con el correo de tu dominio.[/tip]

Seguridad:
- Las contraseñas se guardan cifradas.
- La sesión usa tokens de acceso y de renovación.
- La sesión se cierra sola después de un rato sin uso.`,
  },
  {
    id: 'cap-3',
    number: 3,
    title: 'Roles y permisos',
    content: `Prixia tiene cuatro roles base:

[img:roles]

Administrador de empresa:
- Acceso total dentro de su empresa.
- Gestiona modelos, datos maestros, usuarios, roles y equipos.
- Ve todas las cotizaciones y puede aprobarlas.

Vendedor:
- Crea y edita sus cotizaciones.
- Ve los modelos de precio sin poder cambiarlos.
- Envía cotizaciones a aprobación.
- Ve las suyas y las que le compartieron.

Aprobador:
- Revisa las cotizaciones que esperan su firma.
- Aprueba, rechaza o pide un ajuste.

Administrador de plataforma (Datec):
- Acceso a todas las empresas, para soporte.

El menú lateral:
- Inicio: todos.
- Cotiza, Compite y Comercia: según los permisos del rol.
- Datos maestros e Integraciones: administradores.
- Mi empresa: administradores (los demás ven sólo su perfil).

[importante]El administrador puede crear roles propios en Mi empresa › Roles y elegir, permiso por permiso, qué ve cada uno. Lo que no tiene permiso no aparece en el menú.[/importante]`,
  },
  {
    id: 'cap-4',
    number: 4,
    title: 'Inicio',
    content: `Al entrar, Prixia abre el Inicio: un resumen de los tres módulos y lo que necesita tu atención hoy.

[img:dashboard]

Arriba:
- El saludo y la fecha.
- Accesos rápidos: Nueva cotización, Agregar competidor y Calcular costo puesto.

Las tres tarjetas:
- Cotiza: ingresos aprobados del mes, cotizaciones, tasa de aprobación y pendientes.
- Compite: cuántos productos tienen precio de la competencia y si estás más caro o más barato.
- Comercia: cotizaciones de importación que esperan respuesta y tarifas por vencer.

Necesita tu atención:
- Una lista de lo pendiente (aprobaciones, precios por decidir, cambios de la competencia, tarifas vencidas), con un botón que te lleva directo a resolverlo. Se puede filtrar por módulo.

[nota]El detalle de cotizaciones, márgenes y tendencias está en Cotiza › Visión general y en Cotiza › Analítica.[/nota]`,
  },
  {
    id: 'cap-5',
    number: 5,
    title: 'Datos Maestros',
    content: `Datos Maestros guarda los catálogos que usa todo el sistema: monedas, líneas de negocio, países, materiales y flete. Conviene completarlos antes de empezar a cotizar.

Dónde está:
- En el menú, bajo Datos y cuenta. Lo ve quien tiene ese permiso, normalmente el administrador.
- Cada entrada del menú abre su pestaña en la misma pantalla. Materiales tiene pantalla propia.

Los botones de arriba:
- «Importar CSV» (sólo administradores): bajás la plantilla, la completás y la subís. Al terminar te dice cuántos registros se crearon, cuántos se actualizaron y qué filas fallaron.
- «Crear Nuevo» (o «Nuevo Tipo», en Tipos de cotización) carga un registro a mano.
- «Sincronizar TC», sólo en Divisas.
- «Sincronizar SAP», sólo si tu empresa tiene una integración con SAP S/4HANA activa.

Divisas:
- Cada divisa tiene su código ISO de 3 letras, su símbolo, su nombre y su tasa de cambio contra la divisa base.
- La divisa base va marcada con una estrella: siempre vale 1 y no se puede borrar.
- USDT y USDC se actualizan solas cada 30 minutos con tasas P2P. «Sincronizar TC» fuerza la actualización, y su ícono de historial muestra cómo se movieron.

Tipos de cambio:
- El tipo de cambio de cada par de monedas, por fecha. Se carga a mano con «Nuevo TC» o por CSV.
- Cuando una cotización toma costos de otra en distinta moneda, Prixia usa el tipo de cambio vigente más reciente de ese par. Si falta, la carga se frena hasta que lo registres.

Líneas de negocio:
- Tres niveles: Sector, Vertical y Marca.
- Con «+ Vertical» y «+ Marca» agregás un nivel dentro de otro.
- Cada línea puede tener su margen objetivo y su margen piso.

Países, Tipos de cotización y Centros de beneficio:
- Países: código ISO y nombre.
- Tipos de cotización: las categorías de cotización, cada una con su prefijo de código.
- Centros de beneficio: vienen de SAP y son de sólo lectura.

Incoterms:
- Una matriz que dice qué costo paga el comprador en cada incoterm: flete interno de origen, flete internacional, flete interno de destino, handling, seguro y aranceles.
- Si está vacía, «Cargar Defaults ICC 2020» la completa. Después de tocarla, «Guardar Cambios».

Flete:
- Tramos y tarifas: el catálogo de tramos (origen, destino y modo), con los precios de cada proveedor. Se usan al armar las órdenes de Comercia.
- Proveedores de flete: navieras, agentes de carga y transportistas.
- Servicios de flete: los cargos adicionales, con su precio de referencia.
- Ubicaciones: puertos, terminales y ciudades.

[nota]Los cuatro catálogos de flete dependen del permiso del Tarifario, no del de Datos Maestros: así, quien mantiene el tarifario también mantiene sus proveedores y sus puertos.[/nota]

Materiales:
Es el catálogo de productos y servicios, con dos pestañas: Materiales y Listas de Precios.

- «Nuevo Material»: código, tipo (Producto o Servicio), nombre, línea de negocio, partida arancelaria y gravamen arancelario. Los productos suman SKU, marca, modelo, unidad de medida, peso y medidas.
- Cada cambio de gravamen queda en su historial, con las fechas de cada tasa.
- Al editar un material cargás su precio en cada lista y, si es un producto, su imagen.`,
  },
  {
    id: 'cap-6',
    number: 6,
    title: 'Modelos de precio',
    content: `Un modelo dice cómo se calcula el precio de una cotización: qué datos se cargan y qué fórmulas se aplican. Están en Cotiza › Modelos.

Crear un modelo:
Tocá «Nuevo modelo». Primero elegís una plantilla y después completás nombre, código, descripción y tipo de modelo.

En la lista, cada modelo se puede ver, duplicar, editar o borrar. Dentro de un modelo, «Clonar Modelo» hace una copia.

Las variables:
Son los datos del modelo. Se agregan en la ficha del modelo, con «Nueva Variable».

Tipos de variable:
- Número: cantidades (usuarios, horas).
- Moneda: montos (costo unitario, precio base).
- Porcentaje: descuentos, impuestos.
- Texto y Booleano (sí o no).
- Calculada: sale de una fórmula con otras variables.
- Enum: una lista para elegir.
- Escalonada: precios por volumen, con niveles de cantidad mínima, cantidad máxima, precio y descuento.
- Lookup: busca el valor en una tabla de dos dimensiones.

Fórmulas:
Una variable calculada usa los códigos de otras variables, por ejemplo CANTIDAD * COSTO_UNITARIO. Prixia revisa la fórmula mientras escribís, te dice si es válida o qué está mal, y te muestra las variables que podés usar.

Globales o por línea:
- Si marcás «Variable Global», el valor se comparte en toda la sección de la cotización (por ejemplo, el tipo de cambio o el IVA).
- Si no, cada línea de la cotización tiene su propio valor (por ejemplo, la cantidad).

Más herramientas en la ficha del modelo:
- «Ver Grafo»: qué variable depende de cuál.
- Roles en Cotización: qué variable es el costo, la cantidad, el precio unitario, el total de la línea o el margen.
- Tablas de Lookup: las matrices de dos dimensiones, con «Nueva Tabla».
- El simulador: probá el modelo con valores de ejemplo antes de usarlo.

[tip]Antes de usar un modelo nuevo en una cotización real, probalo en el simulador con números que conozcas.[/tip]`,
  },
  {
    id: 'cap-7',
    number: 7,
    title: 'Nueva cotización',
    content: `Una cotización se arma en Cotiza › Nueva cotización, en un solo formulario. Prixia calcula el precio con el modelo que elijas.

Información Básica:
- Cliente (obligatorio). Si todavía no existe, «Nuevo Cliente» lo crea sin salir del formulario.
- Email del cliente (obligatorio) y teléfono.
- País, línea de negocio (LOB) y divisa (obligatoria).
- Validez en días: 30 por defecto; debajo ves hasta qué fecha vale.
- Incoterm, los códigos de SAP C4C (si los usás) y notas.

Si tu empresa tiene tipos de cotización cargados, arriba aparece además «Tipo de Cotización».

Secciones:
Una cotización puede tener varias secciones, cada una con su propio modelo. Por ejemplo, licencias en una y hardware en otra.

1. En la sección, elegí el «Modelo de Pricing».
2. Completá las «Variables Globales» de la sección.
3. En «Items de Cotización», tocá «Agregar Item», elegí el material y completá la cantidad y las variables de cada línea.
4. Prixia calcula el precio unitario, el total de cada línea y el subtotal de la sección.
5. Para sumar otra sección, tocá «Agregar Sección (Pricer)».

Abajo está el gran total de todas las secciones.

Guardar:
- «Guardar Borrador»: la guarda sin terminar.
- «Crear Cotización»: la termina. Si falta algo, Prixia te dice qué en «Faltan campos para finalizar».
- «Empezar de cero»: limpia el formulario.

Al crearla, Prixia aplica las reglas de aprobación de tu empresa: si no hace falta aprobación, queda aprobada; si hace falta, pasa a En aprobación y le avisa al aprobador.

Estados de una cotización:
- Borrador, En aprobación, Aprobada, Ajuste solicitado, Rechazada, Vencida, Cancelada y Congelada.

[nota]En la lista de cotizaciones el margen va en colores: verde si llega al objetivo, ámbar si queda entre el piso y el objetivo, y rojo si queda debajo del piso.[/nota]`,
  },
  {
    id: 'cap-8',
    number: 8,
    title: 'Aprobaciones',
    content: `En Cotiza › Aprobaciones están las cotizaciones que esperan tu firma. La ven los aprobadores y los administradores. Cuándo una cotización pide aprobación lo decide el administrador en Mi empresa › Aprobaciones.

[img:approval-flow]

Arriba:
- Te toca decidir, Aprobadas por vos y Rechazadas por vos: tocá una para filtrar.
- Tu tiempo promedio de respuesta.

La cola «Por decidir»:
- Cada fila muestra la cotización, el cliente, la justificación del vendedor, el precio, el margen y quién más tiene que firmar.
- Si una cotización es tuya y no te corresponde firmarla, en lugar de los botones aparece «Cotización propia».

Lo que puede hacer el aprobador:
- Aprobar: con un comentario opcional.
- Rechazar: el motivo es obligatorio.
- Solicitar ajuste: sugerís un precio y explicás por qué. Las dos cosas son obligatorias.

[nota]Si la cotización necesita varias firmas, al aprobar Prixia te avisa con «Con tu firma no alcanza»: sigue esperando las firmas que faltan.[/nota]

Cuando piden un ajuste:
Así vuelve la cotización al vendedor y de nuevo al aprobador.

1. El aprobador toca «Solicitar ajuste», sugiere un precio y explica por qué.
2. El vendedor ve en la cotización el aviso «Ajuste Solicitado por Aprobador», con el comentario y el precio sugerido.
3. El vendedor toca «Editar», corrige el precio o las variables y guarda.
4. Toca «Reenviar» y la cotización vuelve a la cola del aprobador.

Debajo de la cola, «Historial» muestra lo que ya se decidió. Se filtra por tipo: Aprobación, Reapertura o Automáticas.

[tip]Antes de aprobar, mirá el margen y la justificación del vendedor: están en la misma fila.[/tip]`,
  },
  {
    id: 'cap-10',
    number: 10,
    title: 'Compite: cómo está armado',
    content: `Compite compara tus precios con los de la competencia y te propone precios nuevos. Nada cambia solo: cada precio espera tu sí, salvo que le des permiso a una estrategia para aplicarlo.

El menú de Compite tiene dos partes.

Día a día:
- Decisiones: los precios que esperan tu sí y cuánta plata hay en juego. El número junto a Decisiones, en el menú, son los que esperan.
- Monitoreo: tus precios contra cada tienda y qué cambió en la competencia.
- Análisis de mercado: cómo evolucionan tus precios y los de la competencia.

Configuración:
- Estrategia: las reglas que limitan los precios y las estrategias que los proponen.
- Productos: tu catálogo, el de cada tienda y los vínculos entre los dos.
- Competencia: las tiendas contra las que te comparás y cuándo se leen.

Cómo se usa, en orden:
Los capítulos que siguen explican cada paso.

1. Cargá tus productos con su precio y su costo (Productos).
2. Revisá qué tiendas comparás (Competencia).
3. Vinculá tus productos con los de cada tienda (Productos › Vincular).
4. Mirá dónde estás caro o barato (Monitoreo).
5. Poné los límites que ningún precio puede pasar (Estrategia › Reglas).
6. Creá estrategias que propongan precios (Estrategia › Estrategias).
7. Aprobá o descartá cada precio (Decisiones).
8. Mandá los precios a tu tienda online (Productos, con «Aplicar a tienda»).

[nota]Lo que antes era «Pricing Dinámico» y «Precio Inteligente / Estrategias de Mercado» hoy está repartido en estas pantallas. Si tenés guardada una dirección vieja, te lleva sola a la pantalla nueva.[/nota]

[tip]Monitoreo y Competencia tienen un botón «Ajustes» (sólo administradores): ahí fijás el margen que busca «Precio inteligente» en cada categoría, cuántos días un precio se considera reciente y desde qué cambio de precio te llega un aviso o un aviso crítico.[/tip]`,
  },
  {
    id: 'cap-11',
    number: 11,
    title: 'Productos',
    content: `En Compite › Productos está tu catálogo. Tiene tres pestañas: Mis productos, Catálogo de las tiendas y Vincular. Este capítulo cuenta la primera; el siguiente, las otras dos.

Mis productos:
- Cada producto con su categoría, precio, costo, margen, en cuántas tiendas se compara y su estado.
- Podés buscar por nombre o SKU. La lista muestra los activos; los archivados se ven en «Archivados».

Los botones de arriba:
- «Exportar»: baja la lista.
- «Nuevo producto» (administradores): lo cargás a mano.
- «Importar CSV» (administradores): carga o actualiza muchos de una vez, con Excel o CSV.
- «Subir fotos» (administradores): un ZIP con las fotos, cada una con el SKU como nombre.
- «Aplicar a tienda» (administradores): manda los precios a tu tienda online conectada.

Cargar un producto:
- Nombre, SKU y precio de hoy (obligatorios).
- Categoría, marca y descripción.
- Costo: sin costo, ninguna regla de margen lo puede cuidar.
- Margen propio de este producto: vacío, usa el de su estrategia; si lo llenás, le gana al de la estrategia.
- En «Inventario y Operación»: stock actual, stock mínimo, días de reposición y velocidad de venta.

La ficha del producto:
- Tocá una fila para abrirla y editar sus datos. Si cambiás el precio, podés anotar el motivo del cambio.
- «Historial» muestra cada cambio de precio, venga de donde venga. Los administradores pueden volver a un precio anterior desde ahí.
- «Ver por qué tiene este precio» explica cómo lo calcula su estrategia con tus reglas de hoy.

Archivar y restaurar (administradores):
- Elegí uno o varios productos con la casilla de la izquierda.
- «Archivar»: salen de tus listas y dejan de calcularse y de compararse, pero conservan su historial y sus vínculos. Un producto no se borra de la plataforma.
- En «Archivados», «Restaurar» los devuelve a la lista. También desde su ficha, con «Producto activo».
- Si probaste Prixia con la demo, los productos de ejemplo quedan marcados «De ejemplo» al pasar al plan completo: archivalos cuando ya no los necesites.

Importar productos:
Tocá «Importar CSV».

1. Tocá «Descargar plantilla (Excel)».
2. Completá la hoja «Productos». Sólo el código, el nombre y el precio son obligatorios. Precio y costo van con impuestos incluidos.
3. Subí el archivo (.xlsx o .csv) y tocá «Revisar antes de importar».
4. Si la revisión está bien, confirmá la importación.

[tip]En la plantilla, una celda vacía no cambia nada. Para borrar un dato, escribí BORRAR en la celda. Si sumás una columna motivo, se guarda como el motivo del cambio de precio.[/tip]

Aplicar a tienda:
«Aplicar a tienda» abre «Enviar precios a tienda»: elegís la tienda, revisás cada cambio (precio anterior y precio nuevo) y tocás «Aplicar cambios». Cada envío queda en Integraciones › Historial de envíos, desde donde se puede revertir.

[importante]Para mandar precios, primero conectá tu tienda en Integraciones › Tiendas conectadas.[/importante]`,
  },
  {
    id: 'cap-12',
    number: 12,
    title: 'Competencia',
    content: `En Compite › Competencia están las tiendas contra las que comparás tus precios.

Tiendas Prixia:
Las tiendas con sitio web las lee Prixia para todas las empresas. No las agregás vos: te las habilita la plataforma y aparecen acá apenas lo hace. Vos elegís si cada una entra o no en tu análisis.

[nota]Si te falta una tienda con sitio web, pedísela a tu contacto de Prixia.[/nota]

Tus tiendas:
- «Agregar tienda» suma una tienda sin sitio web (por ejemplo, un proveedor o un cliente). Sus precios entran a mano.
- «Chequear precios» (administradores) lee en el momento los precios de tus productos vinculados.
- Podés buscar por nombre o dirección y filtrar por categoría y por estado (Encendidas o Apagadas).

Cada tienda:
- Muestra cuándo fue su última lectura y si tiene lecturas programadas.
- El interruptor la apaga o la enciende. Apagada, deja de leerse pero conserva todo su historial.
- En su menú ⋮ (administradores): «Dejar de leer sus precios» o «Leer sus precios», «Cambiar lector» y «Ver lecturas programadas».

Lecturas programadas:
El botón «Lecturas programadas» muestra cuándo se lee cada tienda, qué se leyó y qué falló. Desde ahí también se lee a mano: elegís la tienda y tocás «Chequear precios» o «Leer el catálogo completo».

[nota]Si tu empresa es del rubro comida, la pantalla habla de locales en lugar de tiendas, y al agregar un local podés pegar su página en la plataforma de delivery.[/nota]

[tip]Si una tienda no se lee bien, mirá primero sus lecturas programadas: ahí ves si la última corrida falló.[/tip]`,
  },
  {
    id: 'cap-13',
    number: 13,
    title: 'Catálogo de las tiendas y Vincular',
    content: `Para comparar precios, cada producto tuyo tiene que estar vinculado con el mismo producto en la tienda de la competencia. Eso se hace en dos pestañas de Compite › Productos.

Catálogo de las tiendas:
- Los catálogos leídos de tus tiendas competidoras. Los de las Tiendas Prixia los lee Prixia para todas las empresas que las tienen.
- Por tienda ves el estado de la última lectura, el lector, los artículos leídos, cuántos productos tuyos están vinculados y cuándo terminó.
- «Leer catálogo» pide leer el catálogo de una de tus tiendas competidoras. Mientras corre ves el avance y lo podés cancelar.

Vincular: Prixia propone, vos confirmás:
Prixia busca candidatos, pero nada se vincula solo: cada vínculo lo confirmás vos.

1. En Vincular, elegí el competidor en el selector.
2. Tocá «Ejecutar inferencia». Si tenés más de un competidor leído, «Todas las tiendas» los compara uno tras otro.
3. Revisá la lista «Por vincular»: vinculá cada producto con su opción o descartala.
4. Para decidir varios a la vez, marcalos y usá «Vincular» o «Descartar».

Las listas:
- Por vincular: lo que espera tu decisión.
- Ya vinculados: lo que ya compara precios.
- Descartados: las opciones que dijiste que no.
- Sin ninguna opción: tus productos para los que Prixia no encontró candidatos.

Podés buscar por nombre o código, filtrar por rango de confianza y ordenar por confianza, diferencia de precio, nombre o precio.

Revisar vínculos (administradores):
- En Vincular, la opción «Revisar vínculos» muestra los vínculos que pueden estar mal (por ejemplo, con un precio muy distinto).
- En cada uno elegís «Es este», «Es variante» (otro color o presentación) o «No es».
- Desvincular no borra el historial de precios.

[tip]Empezá por las propuestas de confianza más alta: son las más seguras. Las de confianza baja, miralas con calma antes de vincular.[/tip]`,
  },
  {
    id: 'cap-14',
    number: 14,
    title: 'Monitoreo y Análisis de mercado',
    content: `Compite › Monitoreo muestra tus precios contra los de la competencia y qué cambió en cada tienda. Tiene dos pestañas: Productos y Cambios.

Pestaña Productos:
- Una fila por producto, con tu precio y el de cada tienda.
- «Tienda» muestra sólo los vinculados con una tienda.
- «Comparar contra» elige una tienda; con eso aparece «Orden», para ver primero donde sos más caro o donde sos más barato.
- Filtros rápidos: con vínculo o sin vincular; dato reciente, sin datos recientes o algo retirado; con precio calculado, con oferta, vínculo dudoso, con error o sin precio de competencia.
- «Columnas» elige qué tiendas se ven. «Exportar» baja la tabla.

Tocá un producto para abrir su panel: tu precio, tu costo y tu margen, cuál es la tienda más barata y cuánto más caro o más barato estás. Desde ahí están «Historial» y «Por qué este precio».

Pestaña Cambios:
- Arriba: los cambios de las últimas horas, las lecturas fallidas y la última lectura.
- Lecturas de la competencia: cada vez que Prixia leyó una tienda. Filtrás por Todas, Con cambios o Con error, y tocás una para verla producto por producto.
- Avisos: te avisan cuando una tienda cambia el precio de un producto tuyo, cuando una lectura falla o cuando aparecen productos nuevos. «Marcar todas» los da por vistos.

Análisis de mercado:
Compite › Análisis de mercado muestra cómo evolucionan tus precios y los de la competencia.

- Filtros: el período (de 7 a 90 días, o uno personalizado), la tienda, la categoría y un rango de precio.
- Arriba: cuántos productos tienen competencia, en cuántos sos el más barato, la diferencia promedio y cuántos requieren acción.
- Los gráficos: dónde estás parado frente a cada tienda, qué tan lejos estás del más barato, en qué categorías estás caro o barato, el mapa de oportunidades, la evolución de precios, las mayores variaciones, qué está haciendo la competencia y la cobertura por categoría.

[tip]Mirá la pestaña Cambios después de mandar precios nuevos: ahí ves si la competencia reaccionó.[/tip]`,
  },
  {
    id: 'cap-16',
    number: 16,
    title: 'Compite de punta a punta',
    content: `Ejemplo: tenés una tienda de tecnología y querés que tus precios sigan a la competencia sin perder margen.

Paso 1 — Cargá tus productos:
En Compite › Productos, «Importar CSV» con el código, el nombre, el precio y el costo de cada producto.

Paso 2 — Revisá la competencia:
En Compite › Competencia, mirá qué Tiendas Prixia tenés y elegí cuáles entran en tu análisis. Si te falta una, pedísela a tu contacto de Prixia.

Paso 3 — Vinculá:
En Compite › Productos › Vincular, elegí cada competidor, tocá «Ejecutar inferencia» y confirmá los vínculos.

Paso 4 — Mirá dónde estás:
En Compite › Monitoreo, elegí una tienda en «Comparar contra» y ordená para ver primero donde sos más caro.

Paso 5 — Poné los límites:
En Compite › Estrategia › Reglas, creá «No ganar menos de 8 %» para todo el catálogo y «No mover más de… por vez» con 15 %. Después tocá «Aplicar las reglas y recalcular».

Paso 6 — Creá una estrategia:
En la pestaña Estrategias, «Crear una estrategia»: por ejemplo, «Igualar al más barato» para una marca, todos los días, con «Siempre preguntame».

Paso 7 — Decidí:
En Compite › Decisiones, tocá «Revisar los…» y aprobá o descartá cada precio.

Paso 8 — Mandá los precios a tu tienda:
En Compite › Productos, «Aplicar a tienda». El envío queda en Integraciones › Historial de envíos.

Paso 9 — Seguí a la competencia:
Monitoreo › Cambios te muestra qué movió cada tienda, y Análisis de mercado, las tendencias.

[tip]No hace falta hacer todo de una vez: empezá con una marca y una sola regla, y sumá de a poco.[/tip]`,
  },
  {
    id: 'cap-17',
    number: 17,
    title: 'Estrategias',
    content: `Una estrategia elige productos, calcula su precio con un método y te lo propone para que decidas. Están en Compite › Estrategia, pestaña Estrategias.

Crear una estrategia:
Tocá «Crear una estrategia», arriba a la derecha. Son cuatro pasos.

1. A quién: ponele un nombre (por ejemplo, «Sony al más bajo») y elegí a qué productos alcanza: por categoría y marca, o productos puntuales.
2. Cómo calcula: elegí el método. Ahí mismo ves qué pasaría con tus productos.
3. Cuándo y con qué permiso: cada cuánto revisa, hasta cuándo corre y qué hace cuando propone un precio.
4. Resultados esperados: revisá el resumen y tocá «Crear la estrategia».

Los métodos:
- Igualar al más barato: quedás al nivel del más barato del mercado. Sin competencia leída no calcula.
- Quedar por debajo del más barato: le ganás al más barato por el % que digas. Cada punto que bajás sale de tu margen.
- Apuntar a un margen: cada producto gana lo que decidiste. No mira el mercado y, sin costo cargado, no calcula.
- Precio inteligente: mira el mercado, la demanda y el stock a la vez.
- Bajar un % de mi precio de lista: no necesita costo ni competencia, sirve desde el primer día.

Cuándo corre:
- Cada cuánto revisa: en cuanto lea la competencia, todos los días, una vez por semana o sólo cuando vos lo pidas.
- Hasta cuándo corre: hasta que la apagues, o hasta una fecha. Al vencer, cada producto vuelve a quien lo tenía antes.

El permiso:
- Siempre preguntame: cada precio espera tu sí en Revisar precios.
- Aplica sola: el precio se aplica sin preguntar, siempre dentro de tus reglas.
- Aplica sola, pero preguntame si el cambio supera un %: lo que se mueve poco se aplica solo; un cambio grande te espera.

La lista de estrategias:
- Se ve como Tarjetas, Lista o Por fechas.
- Se filtra por estado (Todas, Activas, Apagadas, Vencidas), por nombre y por fecha de creación.
- Tocá una para ver su ficha: cómo calcula, a quién alcanza, su permiso y cada cuánto revisa.
- «Revisar ahora» la corre en el momento.
- En su menú: «Apagar» o «Encender», «Cambiar permiso», «Duplicar» y «Dar de baja».

[nota]Apagar una estrategia retira lo que tenía esperando en Revisar precios (pasa a Descartadas). Darla de baja no borra las propuestas que ya generó: quedan en el historial con su nombre.[/nota]

[tip]Si todavía no cargaste costos ni tenés competencia leída, «Bajar un % de mi precio de lista» es el método que calcula desde el primer día.[/tip]`,
  },
  {
    id: 'cap-21',
    number: 21,
    title: 'Decisiones y Revisar precios',
    content: `Decisiones:
Compite › Decisiones es la portada del día: cuántos precios esperan tu sí y cuánta plata hay en juego (la diferencia por unidad, por el stock).

- «Por estrategia» lista lo que propone cada una. Tocá una fila para revisar sólo esos precios.
- También aparecen los conflictos entre reglas, los recálculos, las estrategias vencidas y las corridas que fallaron.
- «Para saber» cuenta lo que pasó solo (por ejemplo, precios que se aplicaron solos) y avisa si falta un dato, como productos sin costo.
- Si cambiaste reglas y todavía no las aplicaste, un aviso te lleva a Reglas.

En Decisiones no se decide: el botón «Revisar los…» (o «Revisar todos») abre Revisar precios.

Revisar precios:
Cada precio viene de una estrategia y respeta tus reglas. Arriba elegís qué ver: Por decidir, Aprobadas, Descartadas o Aplicadas solas.

Decidir:
- Cada fila muestra el producto, la estrategia, el precio de hoy, el propuesto, la competencia y el margen.
- Aprobá o descartá desde la fila, o marcá varias y usá «Aprobar» o «Descartar» en la barra de abajo.
- Al descartar podés decir por qué: el dato del mercado está mal, el costo está mal cargado, hay una promoción o un acuerdo, lo cambio yo a mano, prefiero esperar u otro. Si fue sin querer, el aviso te deja «Deshacer».

Los que van de a uno:
- Conflicto entre reglas: no hay un precio que las cumpla todas. Con «Decidir» elegís qué hacer.
- Los que quedan debajo del costo, los que se calcularon hace días y el mercado cambió, o los que usaron una tienda que ya no está entre las tuyas: se aprueban con «Aprobar esta».
- Si el precio o el costo cambiaron después del cálculo, la propuesta queda trabada y no se puede aprobar.

Filtros:
- Por estrategia, con las píldoras de arriba, y por producto o código con el buscador.
- «Filtros» suma categoría, marca, origen (estrategia, recálculo o vuelta de una vencida) y fechas.
- «Sólo con un conflicto entre reglas» y «Sólo cambios grandes».

[nota]Un precio aprobado se puede deshacer: los administradores ven «Volver al precio de antes» en Aprobadas y en Aplicadas solas.[/nota]`,
  },
  {
    id: 'cap-24',
    number: 24,
    title: 'Reglas: cómo funcionan',
    content: `Una regla es un límite que ningún precio puede pasar, lo proponga la estrategia que lo proponga. La estrategia decide a dónde va el precio; la regla sólo dice hasta dónde puede llegar.

Las reglas están en Compite › Estrategia, pestaña Reglas. Arriba elegís qué ver: «Las reglas» o «Cómo queda cada producto».

Tres ideas:

1. Una regla es un límite (en %, o en Bs para un solo producto) y a quién alcanza: todo el catálogo, categorías, marcas o productos puntuales, con excepciones.
2. Si varias alcanzan al mismo producto: del mismo tipo rige la más específica (este producto, después su marca o categoría, después todo el catálogo), aunque pida menos; de tipos distintos se cumplen todas y decide la más exigente (del redondeo corre una sola).
3. Si no hay ningún precio que las cumpla todas, el producto no se mueve solo: queda con un conflicto entre reglas y te espera en Revisar precios.

Los siete límites:
- No ganar menos de… (margen mínimo sobre el precio de venta). Es la única que siempre se cumple. Sin costo cargado, no puede medir.
- No mover más de… por vez, con subida máxima y bajada máxima por separado. Se mide contra el precio de hoy.
- No quedar más de… arriba del más barato. Sin precios de competencia, no puede medir.
- No quedar más de… debajo del más barato: para no regalar margen cuando ya sos el más barato. Sin precios de competencia, no puede medir.
- No vender por debajo de Bs… y No vender por encima de Bs…: un piso o un techo en bolivianos, sólo para un producto.
- Terminar en… (el redondeo). Va al final.

La lista de reglas:
- Cada regla con a quién alcanza, cuántos precios limitó en los últimos 7 días y su estado. El interruptor la enciende o la apaga.
- En cada una: historial, editar y borrar.
- Un cambio en las reglas queda sin aplicar hasta que tocás «Aplicar las reglas y recalcular». El botón muestra cuántos cambios esperan.
- Al recalcular ves, producto por producto, el precio de hoy, el que quedaría y por qué cambia. Los precios nuevos van a Revisar precios para que los decidas.

[tip]En «Cómo queda cada producto» ves, producto por producto, qué pide la estrategia, lo que dejan las reglas y qué propone Prixia al final.[/tip]`,
  },
  {
    id: 'cap-25',
    number: 25,
    title: 'Crear una regla paso a paso',
    content: `Ejemplo: "no ganar menos de 20 % en los productos Sony".

En Compite › Estrategia › Reglas, tocá «Nueva regla». Son tres pasos: Qué límite, A quién y Qué cambiaría.

Paso 1 — Qué límite:
Elegí «No ganar menos de…» y escribí 20.

Paso 2 — A quién:
- Elegí «Por categoría y marca». Las otras opciones son «Todo el catálogo» y «Productos puntuales».
- En Marcas, elegí Sony. Dejá Categorías vacío: alcanza a todo Sony.
- Si hay productos que se venden con poco margen a propósito, sacalos con «Excepciones».
- A la derecha ves a cuántos productos alcanza.

Paso 3 — Qué cambiaría:
- Ves a cuántos alcanza, a cuántos les cambia el precio, cuántos quedarían con un conflicto entre reglas, cuántos no tienen estrategia y cuántos no tienen costo.
- Tocá «Encender la regla», o «Guardar apagada» si todavía no querés que rija.

[nota]La regla nueva queda como cambio sin aplicar: rige cuando tocás «Aplicar las reglas y recalcular», arriba en Reglas. Si salís de Reglas sin aplicarla, Prixia te avisa.[/nota]`,
  },
  {
    id: 'cap-27',
    number: 27,
    title: 'Tiendas conectadas e Historial de envíos',
    content: `Integraciones › Tiendas conectadas guarda las tiendas online a las que Prixia les manda los precios. Los precios se envían desde Compite › Productos, con «Aplicar a tienda». Es para administradores.

Conectar una tienda:
Necesitás la clave que te da tu plataforma de comercio electrónico.

1. Tocá «Conectar tienda».
2. Completá el nombre, la URL de la tienda y la clave (Store Secret / API Key).
3. Tocá «Conectar».

[img:store-connections]

En cada tienda:
- «Probar conexión»: verifica que Prixia llegue a tu tienda.
- «Editar»: cambia la URL o la clave.
- «Desconectar»: la quita. Después ya no se le pueden mandar precios.

[importante]Antes de mandar precios, probá la conexión. Si falla, revisá la URL y la clave.[/importante]

Historial de envíos:
Integraciones › Historial de envíos registra cada envío de precios: qué se mandó, a qué tienda, cuándo y si se aplicó.

[img:push-history]

- Se filtra por tipo (Smart Pricing, Regla, Descuento, Manual) y por estado (Aplicado, Revertido, Fallido).
- Abrí un envío para ver cada producto con su precio anterior y el nuevo.
- «Revertir», en los envíos aplicados, devuelve los precios anteriores a la tienda.`,
  },
  {
    id: 'cap-28',
    number: 28,
    title: 'Claves API',
    content: `Integraciones › Claves API guarda las claves con las que tus sistemas (tu tienda, tu ERP) se conectan con Prixia de forma segura. Es para administradores.

Crear una clave:
Sin una clave, ningún sistema externo puede acceder a tus datos.

1. Tocá «Crear API Key».
2. Escribí un nombre que la identifique (por ejemplo, «Tienda Online»).
3. Copiá la clave y guardala en un lugar seguro.

[importante]La clave completa se muestra una sola vez, al crearla. Si la perdés, creá otra y actualizá tus integraciones.[/importante]

[img:api-keys-mgmt]

Las claves:
- «Claves activas» muestra el nombre, el prefijo, el estado, el último uso y cuántas peticiones recibió cada una.
- «Revocar» la anula: las integraciones que la usen dejan de funcionar en el momento.
- La guía «Cómo usar las claves API» explica los pasos. La clave va en la cabecera X-API-Key.

Probar API:
En la misma pantalla, la tarjeta «Probar API» te deja probar una clave antes de integrar.

1. Pegá la clave.
2. Elegí el tipo de dato: Productos o Inventario.
3. Escribí el cuerpo en JSON y tocá «Enviar».

[nota]El probador de productos sólo revisa: no carga nada en tu catálogo. El de inventario, en cambio, actualiza de verdad el stock de los SKU que mandes.[/nota]`,
  },
  {
    id: 'cap-29',
    number: 29,
    title: 'Integración API',
    content: `Integraciones › Integración API es la guía para que tu equipo técnico conecte sus sistemas con Prixia.

[nota]Es una sección técnica. Si no tenés equipo de desarrollo, no la necesitás para mandar precios: conectá tu tienda en Tiendas conectadas y usá «Aplicar a tienda» en Compite › Productos.[/nota]

Cómo funciona:
- Tu tienda envía su catálogo de productos a Prixia.
- Prixia calcula los precios.
- Tu tienda consulta los precios y los aplica.

Autenticación:
Cada petición lleva la clave en la cabecera X-API-Key. La clave se crea en Integraciones › Claves API.

[img:dp-api-endpoints]

Endpoints disponibles:
- GET /api/v1/price/:sku: el precio activo de un producto.
- POST /api/v1/prices: precios en lote, hasta 100 SKU por llamada.
- GET /api/v1/products: todos los productos con sus precios vigentes.
- POST /api/v1/catalog/sync: sincroniza el catálogo desde tu plataforma.
- GET /api/v1/status: el estado del servicio y si la clave es válida.

La página trae ejemplos listos para copiar en JavaScript, Python, PHP y cURL, y un enlace a la documentación interactiva (Swagger UI), donde se prueban los endpoints desde el navegador.

Límites de uso por plan:
- Trial: 30 peticiones por minuto y 1.000 por día.
- Basic: 100 por minuto y 10.000 por día.
- Professional: 500 por minuto y 50.000 por día.
- Enterprise: 2.000 por minuto y 200.000 por día.

[tip]Pasale el enlace de esta página a tu equipo técnico: tiene los endpoints, los ejemplos, los límites y la documentación.[/tip]`,
  },
  {
    id: 'cap-30',
    number: 30,
    title: 'Una cotización de punta a punta',
    content: `Ejemplo: cotizar software y hardware para un banco.

Antes de empezar (administrador):
Se hace una sola vez.

1. En Datos Maestros, revisá que estén el país, la divisa y los materiales que vas a cotizar.
2. En Cotiza › Modelos, revisá que existan los modelos (por ejemplo, uno de licencias y otro de hardware).
3. En Mi empresa › Aprobaciones, definí cuándo una cotización necesita aprobación y quién la firma.

La cotización (vendedor):
Con el cliente y los modelos listos.

1. En Cotiza › Gestión › Clientes, creá el cliente si todavía no existe.
2. Abrí Cotiza › Nueva cotización y completá cliente, email, país, línea de negocio, divisa y validez.
3. En la primera sección elegí el modelo de licencias, completá las variables globales y agregá los ítems.
4. Tocá «Agregar Sección (Pricer)» y repetí con el modelo de hardware.
5. Revisá el gran total y tocá «Crear Cotización».

La aprobación (aprobador):
Si la cotización la necesita, le llega a su cola.

1. Abrí Cotiza › Aprobaciones.
2. Revisá el precio, el margen y la justificación.
3. Aprobá, rechazá o solicitá un ajuste.

Si pidieron un ajuste:
El vendedor lo ve en la cotización.

1. Abre la cotización, toca «Editar» y corrige el precio.
2. Toca «Reenviar».
3. El aprobador la vuelve a revisar.`,
  },
  {
    id: 'cap-31',
    number: 31,
    title: 'Gestión: clientes y ventas',
    content: `Cotiza › Gestión junta los cuatro pasos de una venta. Arriba hay una barra con los pasos y cada uno abre su pantalla. Sólo ves los que tu rol puede abrir.

Los cuatro pasos:
- Clientes: con quién trabajás.
- Oportunidades: lo que se está por vender.
- Cotizaciones: lo que se cotizó.
- Contratos: lo que se firmó.

Clientes:
- Arriba están «Importar CSV» y «Nuevo cliente».
- Podés buscar por nombre, código, NIT o email, y filtrar por origen: Prixia o HubSpot.
- Cada cliente muestra cuántas cotizaciones tiene.

Crear un cliente:
Tocá «Nuevo cliente».

1. Completá nombre y código (obligatorios).
2. Si querés, elegí el segmento y cargá industria, email, teléfono, NIT, sitio web, dirección y notas.
3. Tocá «Crear Cliente».

[img:customer-segments]

Con «Importar CSV» bajás la plantilla, la completás y la subís: Prixia crea los clientes nuevos y actualiza los que ya existían.

Contactos:
Cada cliente puede tener varios contactos. Tocá la flecha de la fila para verlos.

[img:customer-contacts]

1. Tocá «Agregar Contacto».
2. Completá nombre, apellido y email (obligatorios), teléfono y cargo.
3. Marcá «Contacto principal» si es la persona de referencia.

Cotizaciones:
- La lista tiene dos pestañas: Cotizaciones y Plantillas.
- Podés buscar por código, cliente, modelo o estado, y filtrar por etiqueta.
- Si tu empresa usa el Pricer Datec, aparece un selector para elegir entre las del Pricer Datec y las generales.

Oportunidades y Contratos:
- Oportunidades se sincroniza con HubSpot: «Actualizar ahora» la trae al día. Se filtra por etapa, embudo y monto.
- En Contratos creás contratos y plantillas («Nuevo contrato», «Nueva plantilla») y los filtrás por estado y por tipo.`,
  },
  {
    id: 'cap-33',
    number: 33,
    title: 'Comercia: cómo está armado',
    content: `Comercia calcula cuánto te cuesta realmente importar: del precio del proveedor al producto puesto en tu almacén. También sirve para cotizar fletes y seguir los embarques.

El menú sigue el orden del trabajo:
- Visión general: la importación en curso y cómo se arma el costo.
- Para cotizar: Cotizador y Cotizaciones.
- Para operar: Import / Export (los embarques) y Contenedores.
- Para configurar: Tarifario, Indicadores y Asistente arancelario.

La pantalla Visión general:
- La importación en curso en sus tres pasos: Producto (valor FOB), Logística (flete y seguro) y Costo nacionalizado (costo puesto en destino). Cada paso dice si está listo, en curso o pendiente.
- Cómo se arma el costo: valor FOB, más logística, más tributos, igual a nacionalizado.
- Atajos a Cotizar un flete, Tarifario y Asistente arancelario, con lo que espera en cada uno.
- «Nueva importación» abre una orden nueva. Si hay una en curso, «Continuar donde quedaste» te lleva a ella.

[nota]Lo que ves depende de los permisos de tu rol: el Cotizador, las órdenes, el Tarifario y el Asistente arancelario se habilitan por separado.[/nota]`,
  },
  {
    id: 'cap-34',
    number: 34,
    title: 'Import / Export',
    content: `Comercia › Import / Export muestra los embarques en curso, del zarpe a la entrega.

Arriba:
- Activas, En tránsito, En aduana y Entregadas en los últimos 30 días.

Dos vistas:
- Pipeline (la que se abre primero): una columna por estado, de Cotización a Entregada. Las canceladas van aparte. Cada tarjeta avisa si llega hoy, mañana o con cuántos días de retraso.
- Lista: número de orden, tipo (importación o exportación), cliente, avance, incoterm, FOB, landed cost, fechas y productos.
- Podés buscar por número de orden o proveedor y, con «Filtros», filtrar por estado, proveedor y fechas.

Crear una orden:
Tocá «Nueva orden». Es un asistente de tres pasos.

1. Datos: tipo de operación (importación o exportación), cliente final, fecha, incoterm, origen y destino. En «Más opciones», términos de pago y notas. Tocá «Crear y continuar».
2. Items: «Agregar item», con descripción, cantidad, peso, volumen y total FOB.
3. Tramos: armá el recorrido con «Agregar del catálogo» y poné el precio de cada tramo. Tocá «Finalizar».

[nota]Lo que crea «Nueva orden» es una cotización: pasa a ser orden cuando la convertís desde su ficha, con «Convertir a orden».[/nota]

Estados de una orden:
- Cotización, Borrador, Confirmada, Embarcada, En tránsito, En aduana, Retenida en aduana, Entregada y Cancelada.

[img:import-statuses]`,
  },
  {
    id: 'cap-35',
    number: 35,
    title: 'La ficha de una orden',
    content: `Tocá una orden (o una cotización de Comercia) para abrir su ficha.

Arriba:
- El número, el estado, el cliente y el costo nacionalizado (o «Pendiente»).
- En una cotización: «PDF para el cliente» y «Convertir a orden».
- «Generar documento»: el HBL o el AWB de cada tramo internacional, con la orden confirmada.
- «Compartir con cliente»: un enlace para que tu cliente siga el embarque. Se habilita al confirmar la orden.
- Los botones de estado: «Confirmar Orden», «Marcar en tránsito» y «Marcar Entregada». En el menú, «Cancelar Orden» y «Cambio directo».

Debajo está el recorrido con sus tramos (lo cuenta el capítulo siguiente) y las pestañas.

Las pestañas:
- General: fechas, condiciones comerciales (incoterm, pago, moneda), los tramos con su costo, su precio de venta y su margen, los servicios y los totales.
- Productos: cada producto con su código HS, cantidad, precio FOB, peso, arancel y costo puesto por unidad. Se cargan con «Agregar Producto» o con «Subir proforma».
- Costos: el flete por tramo y el desglose del costo nacionalizado (FOB, flete, seguro, aranceles, IVA, internación y otros), con cómo se calcula cada uno.
- Contenedores: aparece si hay tramos marítimos o multimodales. Se habilita al convertir la cotización en orden.
- Documentos: BL, factura comercial, lista de empaque, certificados, declaración aduanera, póliza de seguro y otros. Se habilita cuando la carga zarpa.
- Seguimiento: los eventos del envío. Se habilita cuando la carga zarpa.
- Historial: cada cambio de la orden.

[importante]El costo nacionalizado es el costo real de tu mercadería puesta en destino: FOB, flete, seguro, aranceles, impuestos y otros gastos. La pestaña Costos muestra de dónde sale cada número.[/importante]`,
  },
  {
    id: 'cap-36',
    number: 36,
    title: 'Cotizador y Cotizaciones',
    content: `Cotizador:
Comercia › Cotizador responde «¿Cuánto cuesta traerlo?»: compara rutas y transportistas con el tarifario vigente. Arriba ves cuántas tarifas están vigentes y cuántas vencidas.

1. Elegí para quién es: para vos (importación propia) o para un cliente. «+ Registrar cliente nuevo» lo crea en el momento.
2. Elegí el origen (puerto de embarque), el destino (ciudad de entrega), el equipo (contenedor) y el peso de la carga.
3. Tocá «Buscar rutas».
4. Compará las opciones: Prixia marca la más económica y la más rápida, y avisa si una tarifa está vencida. Podés ordenar por más barata o por más rápida.
5. Tocá «Elegir» en la que te sirve para ver su desglose, por tramo y por actor (naviera, agente de carga, transportista, agencia aduanera, seguro y otros).
6. Tocá «Generar cotización». Queda guardada con su número y su vencimiento, y «Abrir la cotización» te lleva a ella.

[nota]El Cotizador calcula el flete del embarque. El costo de cada producto puesto en destino se calcula en la orden, con sus productos.[/nota]

Cotizaciones:
Comercia › Cotizaciones lista los precios enviados al cliente que todavía no son embarque. «Cotizar un embarque» abre el Cotizador.

- Arriba, el embudo: Borrador, Esperando respuesta, Ganadas y Perdidas. Tocá una para filtrar.
- Se busca por número, cliente o ruta, y se filtra por estado (Borrador, Enviada, Aceptada, Perdida, Sin respuesta) y por vigencia (Vigente, Por vencer, Vencida).
- Cada fila dice cuánto le queda de vigencia, por ejemplo «vence mañana» o «venció hace 3 días».

Qué hacer con una cotización:
- Verla o bajar el PDF para el cliente.
- «Qué pasó con el cliente»: «Marcar como enviada», «El cliente aceptó», «Se perdió», «Nunca contestaron» o «Volver a borrador». Si se perdió, Prixia te pide el motivo.
- «Volver a cotizar»: «Recotizar con precios de hoy» o «Duplicar para otra ruta».
- «Convertir»: la pasa a orden, que sigue en Import / Export.

[tip]La casilla «Incluir las que ya son embarque» muestra también las aceptadas que ya se convirtieron en orden.[/tip]`,
  },
  {
    id: 'cap-37',
    number: 37,
    title: 'Los tramos de una orden',
    content: `Una importación rara vez es un solo viaje: un contenedor de China a Bolivia puede ir en barco hasta Arica y en camión hasta Santa Cruz. Cada uno de esos pasos es un tramo, con su modo de transporte, sus fechas, su transportista y su referencia de seguimiento.

De dónde salen los tramos:
- Del catálogo de Datos Maestros › Tramos y tarifas, donde cada tramo (origen, destino y modo) tiene los precios de cada proveedor.
- Se agregan al crear la orden (paso Tramos, «Agregar del catálogo») o después, en la ficha, con «+ Agregar tramo» («Agregar el primer tramo» si no tiene ninguno).
- Al agregar o editar un tramo podés buscarlo en el catálogo o cargar a mano origen, destino, modo, tipo de tramo, fechas, transportista y referencia.

Los tipos de tramo son Inland origen, Internacional, Inland destino y Handling.

El recorrido:
En la ficha de la orden, arriba de las pestañas, está el recorrido: la ruta con su cantidad de tramos, el flete total y el avance. Cada tramo muestra su estado, las fechas esperadas y reales, el proveedor, la referencia y el costo.

Avanzar un tramo:
Así se marca el progreso real del viaje.

1. Cuando el tramo arranca, tocá «Iniciar»: pasa a En curso y se guarda la fecha real de inicio.
2. Cuando llega, tocá «Completar»: pasa a Completado y se guarda la fecha real de fin.
3. Seguí con el tramo siguiente.

[importante]Los tramos se mueven en orden y sólo con la orden confirmada: mientras tanto se lee «Se habilita al confirmar la orden», y cada tramo espera a que se complete el anterior. En una orden cancelada o entregada, el recorrido queda de sólo lectura.[/importante]

Los estados de un tramo son Pendiente, En curso, Completado, Atrasado y Cancelado. Con «Editar», en cada tramo, corregís fechas reales, transportista, referencia (BL, AWB, guía) o estado.

Registrar eventos:
En la pestaña Seguimiento, «Registrar Evento» anota algo que pasó: la salida del puerto, el arribo, una inspección. Elegís estado, título, fecha, ubicación y descripción, y en «Tramo (opcional)» a qué tramo pertenece. Sin tramo, queda como evento general.`,
  },
  {
    id: 'cap-38',
    number: 38,
    title: 'Visión general y Analítica',
    content: `Visión general:
Cotiza › Visión general es el resumen de tus cotizaciones. Arriba, lo que necesita tu atención (por ejemplo, cotizaciones por vencer o un margen promedio bajo) y el botón «Nueva Cotización».

- Este mes: total de cotizaciones, aprobadas, pendientes, perdidas e ingresos aprobados.
- La tendencia de los últimos 6 meses y la distribución por estado.
- El pronóstico y la salud del pipeline.
- Los 5 clientes principales, el margen real y los modelos más usados.
- Las cotizaciones recientes, las que están por vencer y las no aprobadas.

Analítica:
Cotiza › Analítica es el análisis en detalle. Elegís el período (7 días, 30 días, 90 días o 12 meses) y arriba ves ingresos, cotizaciones, tasa de cierre, margen promedio, ticket promedio y ciclo promedio.

Las pestañas de Analítica:
- Resumen: ingresos por mes, cotizaciones por estado, ingresos por línea de negocio, márgenes, mejores vendedores y embudo de conversión.
- Márgenes: por línea de negocio, por vendedor y por cliente.
- Ganadas y perdidas: la tasa de ganancia y la comparación entre ganadas y perdidas.
- Descuentos: el descuento promedio y cómo se reparte.
- Modelos: qué tan bien rinde cada modelo, con su margen objetivo contra el margen real.
- Tendencias: la evolución mes a mes.
- Cascada: del costo base al precio neto, pasando por el margen y el descuento.

Inteligencia de Pricing:
Cotiza › Inteligencia de Pricing reúne herramientas de análisis con IA, en cuatro pestañas: Simulador, Predicciones, Recomendaciones e Insights.

Cotizador Solar:
Cotiza › Cotizador Solar cotiza sistemas fotovoltaicos On-Grid para Bolivia.

- «Nueva cotización» abre el cotizador, en tres pasos: Cliente, Ubicación + Panel y Consumo.
- La cotización solar se envía con su propio botón «Enviar a aprobación».
- La lista muestra la potencia (kWp), los paneles, la inversión neta, el retorno (ROI) y el estado. Los administradores ajustan los valores de cálculo en «Parámetros».`,
  },
  {
    id: 'cap-39',
    number: 39,
    title: 'Contenedores, Tarifario e Indicadores',
    content: `Contenedores:
- Comercia › Contenedores muestra qué hay disponible y dónde, por puerto.
- Arriba: contenedores disponibles, puertos activos, metros cúbicos libres y llenado promedio.
- Un mapa de disponibilidad, con el nivel de llenado de cada puerto.
- La lista de contenedores disponibles se filtra por puerto y se ordena por llenado, volumen disponible o fecha de salida. Cada uno lleva a su orden.

Armar la carga de un contenedor:
Desde la pestaña Contenedores de una orden («Agregar Contenedor»), abrí el contenedor.

1. Tocá «Traer los productos de la orden» y después «Estibar».
2. En el editor 3D acomodá la carga con «Auto-acomodar», «Rotar» y «Quitar».
3. Si algo no entra, Prixia te avisa y te ofrece «Crear otro contenedor».
4. Tocá «Guardar acomodo».

Tarifario:
Comercia › Tarifario guarda las tarifas negociadas con navieras y transportistas. Se cargan del Excel y se corrigen ahí mismo. Arriba ves cuántas están vigentes y cuántas vencidas, y un aviso de las que vencen pronto.

1. En «Cargar el tarifario de la semana», arrastrá el Excel o tocá «Elegir archivo». «Descargar plantilla» baja el formato.
2. Prixia te muestra un reporte: qué corrigió sola, qué filas rechazó y qué desgloses no cuadran.
3. Tocá «Confirmar importación» (o «Descartar»).

Más del Tarifario:
- «Las tarifas cargadas»: se filtran por marítimas, terrestres o todas, y se buscan por naviera, puerto o ciudad. Cada una muestra su vigencia y los días que le quedan; «Corregir esta tarifa» la edita.
- «Parámetros de ganancia»: lo que ganás sobre el flete internacional, el terrestre en origen, el terrestre en destino y el manipuleo.
- «Historial de cargas»: cada Excel que se subió.
- En el menú, el número junto a Tarifario son las tarifas vencidas.

Indicadores:
Comercia › Indicadores muestra las órdenes por estado, los días promedio en tránsito y en aduana, la conversión de cotizaciones, los proveedores con más volumen, la tendencia por mes y las órdenes con sobrecosto alto sobre el FOB.`,
  },
  {
    id: 'cap-40',
    number: 40,
    title: 'Asistente arancelario',
    content: `Comercia › Asistente arancelario busca la partida arancelaria de tu producto y te dice qué paga antes de importar. El arancel es ley nacional y lo mantiene Datec: es el mismo para todos los clientes.

Buscar una partida:
- Escribí una mercadería (por ejemplo, algodón o generador) o un código (por ejemplo, 8471) y tocá «Buscar».
- «Solo con ICE» muestra sólo las partidas que pagan ICE.
- También podés navegar por capítulo.

La ficha de la partida:
- Etiquetas de aviso: «No importable», «Paga ICE» o «IVA exento». Si la mercadería no se puede importar, lo ves antes de las pestañas.
- Pestañas: Tributos, Clasificación, Técnico, Preferencias y Documentos.
- En Tributos: el gravamen arancelario (GA), el IVA, el ICE y el IEHD que corresponden.

Liquidar una importación:
En la pestaña Tributos está «Liquidá tu importación de esta partida».

1. Cargá el valor FOB (obligatorio), el flete hasta frontera y el seguro (si lo dejás vacío, se estima).
2. Elegí la vía de transporte, la moneda y el tipo de cambio. «Traer el TC oficial del BCB» lo completa.
3. Tocá «Liquidar».
4. Ves el CIF, cada tributo, el total de tributos, el costo nacionalizado y el costo por unidad, con de dónde sale cada número y qué normas se aplicaron.

Otras vistas:
- Mis aperturas y Mis sinónimos (los términos propios con los que tu empresa encuentra una partida).
- Versiones del arancel.
- Historial y gasto (sólo administradores).

[tip]Con la vía aérea, la liquidación computa el 25 % del flete.[/tip]`,
  },
  {
    id: 'cap-41',
    number: 41,
    title: 'SAP y CRM',
    content: `Integraciones › SAP y CRM es la sincronización con SAP S/4HANA, SAP C4C y HubSpot CRM. Es para administradores.

Configurar una integración:
Cada integración se configura una vez.

1. Tocá «Nueva integración» (o «Configurar primera integración»).
2. Elegí el tipo: SAP S/4HANA (Materiales), SAP C4C (CRM / Oportunidades) o HubSpot CRM.
3. Para SAP: nombre, URL base, versión OData, cada cuántos minutos sincroniza y el tipo de autenticación (Basic, OAuth 2.0 o API Key).
4. Para HubSpot: el token de la app privada y qué objetos sincronizar.

Cada integración tiene su tarjeta:
- La URL, la autenticación, el intervalo, la última y la próxima sincronización.
- «Probar» verifica la conexión. «Importar» y «Exportar» sincronizan a mano (HubSpot no tiene «Exportar»).
- En el menú ⋮: «Editar», «Ver logs» y «Desactivar».

Abajo, el «Historial de Sincronización» muestra cada sincronización: la dirección, el estado, cuántos registros salieron bien, cuántos fallaron o se omitieron, y quién la disparó.

[nota]Con SAP S/4HANA activo, Datos Maestros suma el botón «Sincronizar SAP» para importar desde SAP o exportar a SAP. Las oportunidades de HubSpot se ven en Cotiza › Gestión › Oportunidades.[/nota]`,
  },
  {
    id: 'cap-42',
    number: 42,
    title: 'Mi empresa',
    content: `Mi empresa reúne la configuración de tu empresa, en pestañas: Usuarios, Roles, Equipos, Aprobaciones, Documentos y Configuración. Las administra el administrador; Documentos aparece sólo si tu empresa usa el Pricer Datec.

Usuarios:
- «Nuevo usuario»: nombre, apellido, correo, contraseña (mínimo 8 caracteres) y rol.
- Los roles base son Administrador, Vendedor y Aprobador; también podés asignar los roles propios de tu empresa.
- En cada usuario: editar, restablecer la contraseña y desactivar o activar.
- «Links de invitación»: con «Crear link» elegís el rol y el dominio permitido. Sólo los correos de ese dominio pueden registrarse con ese link.

Roles:
- «Roles base»: qué puede hacer cada rol base y qué ve en el menú. Cada uno tiene «Guardar» y «Restablecer».
- «Roles personalizados»: «Nuevo rol» crea uno a partir de un rol base.
- Con «Permisos» elegís, módulo por módulo, si está Habilitado, Bloqueado (se ve con candado pero no se usa) u Oculto.

Equipos:
- «Nuevo equipo»: nombre, código y descripción.
- En cada equipo: sus miembros («Agregar miembro»), con acceso permanente o hasta una fecha, y sus líneas de negocio, en la pestaña LOBs.
- Sirven para compartir una cotización con todo un equipo de una sola vez.

Aprobaciones:
- «¿Cuándo se pide aprobación?»: sólo si la cotización no cumple el margen, o siempre.
- «Agregar regla» define cuándo se activa una regla y quién firma. Las condiciones pueden ser el monto, el margen, la línea de negocio, el segmento del cliente, el modelo de precios o que supere el presupuesto del cliente.
- Cada regla dice cuántas firmas necesita y quién aprueba, por cargo o por persona.
- El interruptor «Si aplican varias reglas, que firmen todas» decide qué pasa cuando una cotización cumple más de una regla.

Documentos:
Cómo se numeran y qué dicen las cotizaciones que tu empresa envía: la numeración (CITE), las condiciones por defecto, los datos de la oficina, la firma y el anexo técnico.

Configuración:
- General: formato numérico, notificaciones, apariencia (claro, oscuro o como el sistema) y los datos de la empresa (nombre, NIT, dirección, logo y color de la marca).
- Entrenamiento IA e Integraciones (sólo administradores).

[nota]En el mismo grupo del menú están API Developers (la documentación de las API internas, para programadores) y Estado Proyecto (el avance de la implementación).[/nota]`,
  },
];

/* ─── MANUAL SECTIONS (grouped chapters) ──────────────────────── */

// Toma los capítulos por su id, en el orden dado, y los numera desde `startAt`.
function pickAndRenumber(ids: string[], startAt: number): ManualChapter[] {
  return ids.map((id, i) => {
    const ch = manualChapters.find((c) => c.id === id);
    if (!ch) throw new Error(`Chapter ${id} not found`);
    return { ...ch, number: startAt + i };
  });
}

// Las secciones del visor, en el orden del menú. La numeración corre seguida de
// una sección a la otra: el número que se ve sale de acá, no del `number` de cada capítulo.
const SECCIONES: Array<Omit<ManualSection, 'chapters'> & { ids: string[] }> = [
  { id: 'plataforma', title: 'Plataforma', ids: ['cap-1', 'cap-2', 'cap-3', 'cap-4'] },
  {
    id: 'cotiza',
    title: 'Cotiza',
    ids: [
      'cap-7', // Nueva cotización
      'cap-8', // Aprobaciones
      'cap-31', // Gestión
      'cap-6', // Modelos
      'cap-38', // Visión general y Analítica
      'cap-30', // De punta a punta
    ],
  },
  {
    id: 'compite',
    title: 'Compite',
    ids: [
      'cap-10', // Cómo está armado
      'cap-11', // Productos
      'cap-13', // Catálogo de las tiendas y Vincular
      'cap-12', // Competencia
      'cap-14', // Monitoreo y Análisis de mercado
      'cap-24', // Reglas
      'cap-25', // Crear una regla
      'cap-17', // Estrategias
      'cap-21', // Decisiones y Revisar precios
      'cap-16', // De punta a punta
    ],
  },
  {
    id: 'comercia',
    title: 'Comercia',
    ids: [
      'cap-33', // Cómo está armado
      'cap-36', // Cotizador y Cotizaciones
      'cap-34', // Import / Export
      'cap-35', // La ficha de una orden
      'cap-37', // Los tramos
      'cap-39', // Contenedores, Tarifario e Indicadores
      'cap-40', // Asistente arancelario
    ],
  },
  {
    id: 'datos-y-cuenta',
    title: 'Datos y cuenta',
    ids: [
      'cap-5', // Datos Maestros
      'cap-41', // SAP y CRM
      'cap-27', // Tiendas conectadas e Historial de envíos
      'cap-28', // Claves API
      'cap-29', // Integración API
      'cap-42', // Mi empresa
    ],
  },
];

export const manualSections: ManualSection[] = SECCIONES.map(({ id, title, ids }, i) => ({
  id,
  title,
  chapters: pickAndRenumber(
    ids,
    1 + SECCIONES.slice(0, i).reduce((total, s) => total + s.ids.length, 0)
  ),
}));
